import React from "react";
import axios from "axios";
import { Shield, Lock, AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { key: "harassment", label: "Chikane eller diskrimination" },
  { key: "fraud", label: "Bedrageri eller økonomisk uregelmæssighed" },
  { key: "safety", label: "Sikkerhedsbrud eller fareforhold" },
  { key: "data", label: "Brud på databeskyttelse (GDPR)" },
  { key: "corruption", label: "Bestikkelse, korruption eller interessekonflikt" },
  { key: "other", label: "Andet" },
];

export default function WhistleblowerPage() {
  const [subject, setSubject] = React.useState("");
  const [message, setMessage] = React.useState("");
  const [category, setCategory] = React.useState("other");
  const [anonymous, setAnonymous] = React.useState(true);
  const [contactEmail, setContactEmail] = React.useState("");
  const [contactPhone, setContactPhone] = React.useState("");
  const [sending, setSending] = React.useState(false);
  const [done, setDone] = React.useState(null); // {reference_id} or null
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    document.title = "Whistleblower — anonym indberetning · EMA Kurer ApS";
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (subject.trim().length < 3) {
      setError("Emne skal være mindst 3 tegn");
      return;
    }
    if (message.trim().length < 20) {
      setError("Beskrivelse skal være mindst 20 tegn for at vi kan vurdere indberetningen");
      return;
    }
    setSending(true);
    try {
      const payload = {
        subject: subject.trim(),
        message: message.trim(),
        category,
        anonymous,
        contact_email: anonymous ? null : contactEmail.trim() || null,
        contact_phone: anonymous ? null : contactPhone.trim() || null,
      };
      const { data } = await axios.post(`${API}/whistleblower`, payload);
      setDone(data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Kunne ikke sende indberetning. Prøv igen.");
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-brand-bg flex items-center justify-center px-6" data-testid="whistleblower-success">
        <div className="max-w-xl w-full bg-white border border-brand p-10 text-center">
          <div className="w-16 h-16 mx-auto mb-6 bg-emerald-100 flex items-center justify-center rounded-full">
            <CheckCircle2 size={36} className="text-emerald-700" />
          </div>
          <h1 className="font-display font-bold text-3xl text-brand-dark mb-3">Tak — din indberetning er modtaget</h1>
          <p className="text-brand-text2 mb-6">
            Vi behandler din indberetning fortroligt. Hvis du har valgt at være anonym, har vi ingen oplysninger om dig — kun din indberetning.
          </p>
          <div className="bg-brand-bg2 border border-brand p-4 text-left mb-6">
            <div className="kicker mb-1">Reference-ID</div>
            <code className="font-mono text-sm text-brand-dark" data-testid="whistleblower-ref-id">{done.reference_id}</code>
            <p className="text-xs text-brand-text2 mt-2">
              Gem dette ID hvis du senere vil følge op på sagen (uden at afsløre din identitet).
            </p>
          </div>
          <a href="/" className="inline-block bg-brand-dark text-white px-6 py-3 hover:bg-brand-red transition-colors" data-testid="whistleblower-home-link">
            Tilbage til forsiden
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-bg" data-testid="whistleblower-page">
      <div className="max-w-3xl mx-auto px-6 py-16">
        {/* Header */}
        <div className="flex items-center gap-4 mb-2">
          <div className="w-12 h-12 bg-brand-dark flex items-center justify-center">
            <Shield size={22} className="text-white" />
          </div>
          <div className="kicker">Whistleblower · Fortroligt</div>
        </div>
        <h1 className="font-display font-bold text-4xl sm:text-5xl text-brand-dark tracking-tight mb-4" data-testid="whistleblower-title">
          Anonym indberetning
        </h1>
        <p className="text-brand-text2 text-base leading-relaxed mb-4 max-w-2xl">
          Hvis du har oplevet eller bemærket noget der strider mod loven, vores etiske regler eller vores forretningsskik, kan du indberette det her — anonymt eller med kontaktoplysninger.
        </p>
        <div className="bg-emerald-50 border-l-4 border-emerald-500 px-4 py-3 mb-8 text-xs text-emerald-900">
          <strong>Juridisk beskyttelse:</strong> Denne whistleblower-ordning opfylder kravene i{" "}
          <a href="https://www.retsinformation.dk/eli/lta/2021/1436" target="_blank" rel="noreferrer" className="underline font-bold">
            Whistleblowerloven (lov nr. 1436 af 29/06/2021)
          </a>{" "}
          samt{" "}
          <a href="https://eur-lex.europa.eu/eli/dir/2019/1937/oj" target="_blank" rel="noreferrer" className="underline font-bold">
            EU-direktiv 2019/1937
          </a>{" "}
          om beskyttelse af whistleblowere. Du må ikke udsættes for gengældelse (afskedigelse, degradering, chikane m.v.) som
          følge af en indberetning afgivet i god tro. Brud på dette kan medføre erstatningsansvar over for dig og bødestraf.
        </div>

        {/* Trust strip */}
        <div className="grid sm:grid-cols-3 gap-3 mb-10">
          <div className="bg-white border border-brand p-4">
            <Lock size={18} className="text-brand-red mb-2" />
            <div className="font-display font-bold text-sm text-brand-dark">100% fortroligt</div>
            <p className="text-xs text-brand-text2 mt-1">Kun ledelsen kan se din indberetning</p>
          </div>
          <div className="bg-white border border-brand p-4">
            <Shield size={18} className="text-brand-red mb-2" />
            <div className="font-display font-bold text-sm text-brand-dark">Ingen IP-logning</div>
            <p className="text-xs text-brand-text2 mt-1">GDPR-sikker — ingen sporing</p>
          </div>
          <div className="bg-white border border-brand p-4">
            <AlertTriangle size={18} className="text-brand-red mb-2" />
            <div className="font-display font-bold text-sm text-brand-dark">Beskyttelse mod gengældelse</div>
            <p className="text-xs text-brand-text2 mt-1">EMA Kurer følger whistleblower-loven</p>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={onSubmit} className="bg-white border border-brand p-8 space-y-6" data-testid="whistleblower-form">
          <div>
            <label className="block kicker mb-2">Kategori</label>
            <select
              className="w-full border border-brand p-3 text-sm bg-white"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              data-testid="whistleblower-category"
            >
              {CATEGORIES.map((c) => (
                <option key={c.key} value={c.key}>{c.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block kicker mb-2">Emne *</label>
            <input
              type="text"
              className="w-full border border-brand p-3 text-sm"
              placeholder="Kort overskrift på indberetningen"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              maxLength={200}
              required
              data-testid="whistleblower-subject"
            />
          </div>

          <div>
            <label className="block kicker mb-2">Beskriv hvad der er sket *</label>
            <textarea
              className="w-full border border-brand p-3 text-sm min-h-[180px] font-sans"
              placeholder="Beskriv hændelsen så detaljeret du kan — hvad, hvor, hvornår, hvem var involveret. Jo flere konkrete detaljer, desto bedre kan vi efterforske sagen."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              maxLength={10000}
              required
              minLength={20}
              data-testid="whistleblower-message"
            />
            <div className="text-xs text-brand-text2 mt-1 text-right">{message.length}/10.000 tegn</div>
          </div>

          <div className="border-t border-brand pt-6">
            <div className="flex items-start gap-3 mb-4">
              <input
                type="checkbox"
                id="anon-toggle"
                checked={anonymous}
                onChange={(e) => setAnonymous(e.target.checked)}
                className="mt-1"
                data-testid="whistleblower-anonymous"
              />
              <label htmlFor="anon-toggle" className="flex-1 cursor-pointer">
                <div className="font-display font-bold text-sm text-brand-dark">Jeg vil være anonym</div>
                <p className="text-xs text-brand-text2 mt-1">
                  Vi får kun din indberetning, ikke din identitet. Du kan stadig følge sagen med dit reference-ID.
                </p>
              </label>
            </div>

            {!anonymous && (
              <div className="space-y-4 bg-brand-bg2 border border-brand p-5">
                <p className="text-xs text-brand-text2">
                  Hvis du giver kontaktoplysninger, kan vi følge op med dig og evt. give status på sagen.
                </p>
                <div>
                  <label className="block kicker mb-2">Email (valgfrit)</label>
                  <input
                    type="email"
                    className="w-full border border-brand p-3 text-sm"
                    value={contactEmail}
                    onChange={(e) => setContactEmail(e.target.value)}
                    data-testid="whistleblower-contact-email"
                  />
                </div>
                <div>
                  <label className="block kicker mb-2">Telefon (valgfrit)</label>
                  <input
                    type="tel"
                    className="w-full border border-brand p-3 text-sm"
                    value={contactPhone}
                    onChange={(e) => setContactPhone(e.target.value)}
                    data-testid="whistleblower-contact-phone"
                  />
                </div>
              </div>
            )}
          </div>

          {error && (
            <div className="bg-red-50 border border-red-300 text-red-900 p-3 text-sm" data-testid="whistleblower-error">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={sending}
            className="w-full bg-brand-red text-white font-display font-bold py-4 hover:bg-red-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            data-testid="whistleblower-submit"
          >
            {sending ? (
              <><Loader2 size={18} className="animate-spin" /> Sender sikkert…</>
            ) : (
              <><Shield size={18} /> Send indberetning</>
            )}
          </button>

          <p className="text-xs text-brand-text2 text-center">
            EMA Kurer ApS · CVR 46465717 · Indberetninger behandles efter whistleblower-loven og GDPR.
          </p>
        </form>

        <div className="mt-8 bg-white border border-brand p-6 text-sm text-brand-text2" data-testid="external-channels">
          <div className="font-display font-bold text-brand-dark mb-2">Ekstern kanal — Datatilsynet</div>
          <p className="mb-2">
            Du har <strong>ret</strong> til at indberette eksternt — fx hvis du ikke ønsker at indberette internt eller hvis du
            ikke har tillid til at sagen vil blive behandlet objektivt her. Den eksterne myndighed er typisk:
          </p>
          <ul className="list-disc pl-5 space-y-1 text-xs">
            <li>
              <a href="https://www.datatilsynet.dk/whistleblower" target="_blank" rel="noreferrer" className="text-brand-red underline">
                Datatilsynets eksterne whistleblower-ordning
              </a>
            </li>
            <li>
              Eller direkte til den relevante tilsynsmyndighed (Politi, Skattestyrelsen, Arbejdstilsynet osv.)
            </li>
          </ul>
          <p className="mt-3 text-xs">
            Lovgrundlag: <strong>Lov nr. 1436 af 29/06/2021</strong> om beskyttelse af whistleblowere (whistleblowerloven), § 13.
          </p>
        </div>
      </div>
    </div>
  );
}
